import { useCallback, useContext, useState } from 'react';

import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import {
  setNotifyCounter,
  trackNotifyEvent,
} from '@/common/analytics/gtm/shared/trackNotify/trackNotifyAnalytics';
import { NotifyMeProps } from '@/common/components/NotifyButton/NotifyButton.interface';
import {
  useSubscribeToEvent,
  useSubscribedEvents,
  useUnsubscribeFromEvent,
} from '@/common/components/NotifyButton/queries';
import { ToastType } from '@/common/components/Toast/Toast';
import { ToastNotification } from '@/common/components/Toast/ToastNotification/ToastNotification';
import { DEFAULT_ERROR_TEXT } from '@/common/constants/labels';
import useModalContext from '@/common/contexts/ModalContext/useModalContext';
import useToastContext from '@/common/contexts/ToastContext/useToastContext';
import SvgIconAlarmBell from '@/common/icons/generated/notify/IconAlarmBell';
import SvgIconAlarmBellStruck from '@/common/icons/generated/notify/IconAlarmBellStruck';
import { Button } from '@/common/styleguide/atoms/Button/Button';
import {
  LocalStorageManager,
  LocalStorageData,
  LocalStorageKey,
} from '@/common/utils/browserStorage/LocalStorageManager';
import { AuthContext } from '@/contexts/auth/AuthContext';
import { SubscribedEvents } from 'generatedBfw/static';

import styles from './notifyButton.module.scss';

const FirstEventPopup = dynamic(
  () => import('@/common/components/NotifyButton/components/FirstEventPopup/FirstEventPopup'),
  {
    ssr: false,
  },
);

const FirstEventLegalInfoPopup = dynamic(
  () =>
    import(
      '@/common/components/NotifyButton/components/FirstEventLegalInfoPopup/FirstEventLegalInfoPopup'
    ),
  {
    ssr: false,
  },
);

const DynamicSignInToNotifyPopup = dynamic(
  () => import('./components/SignInToNotifyPopup/SignInToNotifyPopup'),
  {
    ssr: false,
  },
);

const localStorageManager = new LocalStorageManager<
  LocalStorageData[LocalStorageKey.NOTIFY_BUTTON_METADATA]
>(LocalStorageKey.NOTIFY_BUTTON_METADATA);

export const NotifyButton = ({
  className = '',
  eventId,
  isFullWidth,
  metadata,
  buttonProps,
  onClick,
}: NotifyMeProps) => {
  const { displayModal, closeModal } = useModalContext();
  const { displayToast } = useToastContext();
  const { userInfo, fetchedUserInfo } = useContext(AuthContext);
  const {
    query: { isFirstEvent: isFirstEventQueryParam },
  } = useRouter();

  const notifyMetadata = localStorageManager.getItem();
  const isMatchingMetadata =
    notifyMetadata?.eventId === eventId && notifyMetadata.componentName === metadata.componentName;

  const {
    data: subscribedEvents,
    isFetching: isFetchingEvents,
    refetch: refetchSubscribedEvents,
  } = useSubscribedEvents({
    enabled: fetchedUserInfo && userInfo?.isUserLoggedIn,
    onSuccess: (data: SubscribedEvents) => {
      const { events, hasEverSubscribedToEvent } = data;
      setIsSubscribed(events.includes(eventId));
      const eventsCount = events.length;

      if (
        isMatchingMetadata &&
        notifyMetadata.subscribedEventsCount < eventsCount &&
        events.includes(notifyMetadata.eventId)
      ) {
        trackNotifyEvent(metadata, {
          type: 'notify_me',
          userActionType: 'click',
          notifyCounter: eventsCount.toString(),
          firstTime: hasEverSubscribedToEvent ? 'no' : 'yes',
        });
      }
      localStorageManager.removeItem();
    },
  });
  const hasEverSubscribedToEvent = subscribedEvents?.hasEverSubscribedToEvent;
  const subscribedEventsCount = subscribedEvents?.events?.length ?? 0;

  const [isSubscribed, setIsSubscribed] = useState(subscribedEvents?.events?.includes(eventId));

  const [isFirstEventPopupActive, setIsFirstEventPopupActive] = useState(
    isMatchingMetadata && isFirstEventQueryParam === 'true',
  );

  const [prevIsFirstEventPopupActive, setPrevIsFirstEventPopupActive] = useState(false);

  const handleFirstEventInfoPopupClose = useCallback(
    (suppressAnalytics?: boolean) => {
      setIsFirstEventPopupActive(false);
      closeModal();
      if (suppressAnalytics) return;

      trackNotifyEvent(metadata, {
        type: 'notify_me',
        userActionType: 'close_click',
        firstTime: 'yes',
      });
    },
    [metadata, closeModal],
  );

  const showFirstEventPopup = () =>
    displayModal(<FirstEventPopup metadata={metadata} onClose={handleFirstEventInfoPopupClose} />);

  if (isFirstEventPopupActive && prevIsFirstEventPopupActive !== isFirstEventPopupActive) {
    showFirstEventPopup();
    setPrevIsFirstEventPopupActive(isFirstEventPopupActive);
  }

  const [isLegalInfoAgreed, setIsLegalInfoAgreed] = useState(false);

  const { mutate: subscribe, isLoading: isSubscribing } = useSubscribeToEvent(eventId, {
    onSuccess: (data) => {
      if (data.isFirstEvent) {
        showFirstEventPopup();
      }

      trackNotifyEvent(metadata, {
        type: 'notify_me',
        userActionType: 'click',
        notifyCounter: setNotifyCounter({
          counter: subscribedEventsCount,
          type: 'increment',
        }),
        firstTime: hasEverSubscribedToEvent ? 'no' : 'yes',
      });

      refetchSubscribedEvents();
      displayToast({ children: <ToastNotification text="Email notification activated" /> });
    },
    onError: () => {
      toggleSubscribeButton();
      displayToast({ type: ToastType.ERROR, children: DEFAULT_ERROR_TEXT });
    },
  });

  const { mutate: unsubscribe, isLoading: isUnsubscribing } = useUnsubscribeFromEvent(eventId, {
    onSuccess: () => {
      trackNotifyEvent(metadata, {
        type: 'do_not_notify_me',
        userActionType: 'click',
        notifyCounter: (subscribedEvents?.events.length ?? 0).toString(),
        firstTime: 'no',
      });
      refetchSubscribedEvents();
      displayToast({ children: <ToastNotification text="Email notification deactivated" /> });
    },
    onError: () => {
      toggleSubscribeButton();
      displayToast({ type: ToastType.ERROR, children: DEFAULT_ERROR_TEXT });
    },
  });

  const classes = clsx(
    {
      [styles.button]: !isFullWidth,
      [styles.buttonActive]: !isFullWidth && isSubscribed,
      [styles.reset]: !isFullWidth,

      [styles.fullWidth]: isFullWidth,
      [styles.fullWidthActive]: isFullWidth && isSubscribed,
    },
    className,
  );

  const toggleSubscribeButton = () => {
    setIsSubscribed((prev) => !prev);
  };

  const checkIfButtonIsDisabled = () => {
    // enable button if user is not logged in
    if (!userInfo?.isUserLoggedIn) {
      return false;
    }
    return isFetchingEvents || isSubscribing || isUnsubscribing; // disable button on fetching events/subscribing/unsubscribing
  };

  const handleNotifyButtonClick = () => {
    onClick?.();
    if (!userInfo?.isUserLoggedIn) {
      displayModal(
        <DynamicSignInToNotifyPopup
          eventId={eventId}
          metadata={metadata}
          onClose={closeModal}
          subscribedEventsCount={subscribedEventsCount}
        />,
      );
      return;
    }
    if (
      !hasEverSubscribedToEvent &&
      !isLegalInfoAgreed &&
      !hasEverSubscribedToEvent &&
      !isLegalInfoAgreed &&
      !isSubscribed
    ) {
      displayModal(
        <FirstEventLegalInfoPopup
          metadata={metadata}
          onClose={closeModal}
          setIsLegalInfoAgreed={setIsLegalInfoAgreed}
          subscribe={subscribe}
          toggleSubscribeButton={toggleSubscribeButton}
        />,
      );
      return;
    }

    toggleSubscribeButton();
    if (isSubscribed) {
      unsubscribe();
    } else {
      subscribe();
    }
  };

  const prepareButtonContent = () => {
    if (isFullWidth && isSubscribed)
      return (
        <>
          Do not notify me <SvgIconAlarmBellStruck />
        </>
      );
    else if (isFullWidth && !isSubscribed)
      return (
        <>
          Notify me <SvgIconAlarmBell />
        </>
      );
    else if (!isFullWidth && isSubscribed) return <SvgIconAlarmBellStruck />;
    else return <SvgIconAlarmBell />;
  };

  return (
    <Button
      className={classes}
      data-testid="notify-button"
      disabled={checkIfButtonIsDisabled()}
      onClick={handleNotifyButtonClick}
      variant={isSubscribed ? 'secondary' : 'primary'}
      {...buttonProps}
    >
      {prepareButtonContent()}
    </Button>
  );
};

export default NotifyButton;
